import type { Bordereau, Exercice } from '@mairistem/comptabilite';
import { isNewIdentifiant } from '@mairistem/tools';
import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import BordereauActionType from '../../../../../interfaces/pieces/bordereaux/BordereauActionType';
import type { BordereauWithState } from '../../../../../interfaces/pieces/TreeRow';
import type TreeviewActionResult from '../../../../../interfaces/pieces/TreeviewActionResult';
import getUserInfo from '../../../../../services/parametres/userInfo';
import BordereauModal from '../../../../Mouvement/FichePanel/InformationsGenerales/BordereauModal';
import BordereauDeleteModal from './BordereauDeleteModal';
import BordereauDenumeroterModal from './BordereauDenumeroterModal';
import BordereauForceEtatAckModal from './BordereauForceEtatAckModal';
import BordereauForcePriseEnChargeModal from './BordereauForcePriseEnChargeModal';
import BordereauMettreEnEchecModal from './BordereauMettreEnEchecModal';
import BordereauNumeroterModal from './BordereauNumeroterArchiverModal';
import BordereauRejeterCollectiviteModal from './BordereauRejeterCollectiviteModal';
import DuplicationModal from './DuplicationModal';
import EmettreFlux360Modal from './EmettreFlux360Modal';
import RelancerFluxBordereauModal from './RelancerFluxBordereauModal';

interface BordereauModalProviderProps {
	bordereau: BordereauWithState;
	exercice: Exercice;
	treeViewAction: TreeviewActionResult<BordereauWithState>;
	onChange: (
		bordereauActionType: BordereauActionType,
		data: BordereauWithState,
	) => void;
}

const BordereauModalProvider = ({
	treeViewAction,
	onChange,
	bordereau,
	exercice,
}: BordereauModalProviderProps) => {
	const [data, setData] = React.useState<any>(treeViewAction.extraProps);
	const [bordereauAffichageJour, setBordereauAffichageJour] =
		React.useState<number>(0);

	const handleCloseModal = (modalName: string): void => {
		setData((old) => ({ ...old, [modalName]: { visible: false } }));
	};

	const handleFetchBordereauAffichageJour = async (): Promise<void> => {
		getUserInfo(
			new URLSearchParams({
				'column[]': 'inf_bord_affichage_jour',
			}),
		)
			.then((response) =>
				setBordereauAffichageJour(response.data[0].bordAffichageJour),
			)
			.catch(() =>
				toast.error(
					"Une erreur est survenue lors de la récupération de l'information d'affichage des jours du bordereau",
				),
			);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: Pas besoin de +
	useEffect(() => {
		setData(treeViewAction.extraProps);
		handleFetchBordereauAffichageJour();
	}, [treeViewAction.extraProps]);

	const isSuperUser: boolean = data?.isSuperUser;

	if (data?.bordereauModal?.visible) {
		return (
			<BordereauModal
				isSuperUser={isSuperUser}
				sensDisabled
				open={data.bordereauModal?.visible}
				isCreation={isNewIdentifiant(bordereau?.identifiant)}
				entite={exercice.entite}
				exercice={exercice}
				bordereau={bordereau}
				onConfirm={(updatedBordereau: Bordereau) =>
					onChange(data?.bordereauModal?.typeAction, updatedBordereau)
				}
				onClose={() => handleCloseModal('bordereauModal')}
			/>
		);
	}

	if (data?.deleteModal?.visible) {
		return (
			<BordereauDeleteModal
				onClose={() => handleCloseModal('deleteModal')}
				onChange={() => onChange(BordereauActionType.SUPPRIMER, bordereau)}
				visible={data.deleteModal?.visible}
				isSuperUser={isSuperUser}
				bordereau={bordereau}
			/>
		);
	}

	if (data?.numeroterModal?.visible) {
		return (
			<BordereauNumeroterModal
				isSuperUser={isSuperUser}
				onChange={(updatedBordereau) =>
					onChange(BordereauActionType.NUMEROTER, updatedBordereau)
				}
				onClose={() => handleCloseModal('numeroterModal')}
				visible={data.numeroterModal?.visible}
				bordereau={bordereau}
				isArchive={data.numeroterModal?.isArchive}
				nombreJourAffichage={bordereauAffichageJour}
			/>
		);
	}

	if (data?.relancerFluxBordereauModal?.visible) {
		return (
			<RelancerFluxBordereauModal
				onChange={(updatedBordereau) =>
					onChange(BordereauActionType.RELANCER_FLUX, updatedBordereau)
				}
				onClose={() => handleCloseModal('relancerFluxBordereauModal')}
				bordereau={bordereau}
				visible={data.relancerFluxBordereauModal?.visible}
				isSuperUser={isSuperUser}
			/>
		);
	}

	if (data?.emettreFlux360Modal?.visible) {
		return (
			<EmettreFlux360Modal
				onChange={(updatedBordereau) =>
					onChange(BordereauActionType.EMETTRE_LE_FLUX_360, updatedBordereau)
				}
				onClose={() => handleCloseModal('emettreFlux360Modal')}
				bordereau={bordereau}
				visible={data.emettreFlux360Modal?.visible}
			/>
		);
	}

	if (data?.relancerFlux360Modal?.visible) {
		return (
			<EmettreFlux360Modal
				isRelance
				isSuperUser={isSuperUser}
				onChange={(updatedBordereau) =>
					onChange(BordereauActionType.RELANCER_FLUX_360, updatedBordereau)
				}
				onClose={() => handleCloseModal('relancerFlux360Modal')}
				bordereau={bordereau}
				visible={data.relancerFlux360Modal?.visible}
			/>
		);
	}

	if (data?.bordereauRejeterCollectiviteModal?.visible) {
		return (
			<BordereauRejeterCollectiviteModal
				estAnnulation={data.bordereauRejeterCollectiviteModal?.estAnnulation}
				onValidate={(updatedBordereau) =>
					onChange(
						data.bordereauRejeterCollectiviteModal?.estAnnulation
							? BordereauActionType.ANNULER_REDUIRE
							: BordereauActionType.REJETER_COLLECTIVITE,
						updatedBordereau as unknown as BordereauWithState,
					)
				}
				onClose={() => handleCloseModal('bordereauRejeterCollectiviteModal')}
				bordereau={bordereau}
				visible={data.bordereauRejeterCollectiviteModal?.visible}
				isSuperUser={isSuperUser}
			/>
		);
	}

	if (data?.bordereauDenumeroterModal?.visible) {
		return (
			<BordereauDenumeroterModal
				visible={data?.bordereauDenumeroterModal?.visible}
				bordereau={bordereau}
				onChange={(bordereauModifie) =>
					onChange(BordereauActionType.DENUMEROTER, bordereauModifie)
				}
				onClose={() => handleCloseModal('bordereauDenumeroterModal')}
				isSuperUser={isSuperUser}
				isArchive={data?.bordereauDenumeroterModal?.isArchive}
			/>
		);
	}

	if (data?.bordereauDuplicationModal?.visible) {
		return (
			<DuplicationModal
				visible={data.bordereauDuplicationModal?.visible}
				onValidate={async (bordereauCree) => {
					onChange(BordereauActionType.DUPLIQUER, {
						...bordereauCree,
						montant: bordereau?.montant,
					} as unknown as BordereauWithState);
				}}
				onClose={() => handleCloseModal('bordereauDuplicationModal')}
				bordereau={bordereau}
			/>
		);
	}

	if (data?.bordereauForceEtatAcquittement?.visible) {
		const isAcquittement = data.bordereauForceEtatAcquittement?.isAcquittement;
		return (
			<BordereauForceEtatAckModal
				visible={data.bordereauForceEtatAcquittement?.visible}
				isAcquittement={isAcquittement}
				onChange={(bordereauModifie) =>
					onChange(
						isAcquittement
							? BordereauActionType.FORCE_ACK
							: BordereauActionType.FORCE_NACK,
						bordereauModifie,
					)
				}
				onClose={() => handleCloseModal('bordereauForceEtatAcquittement')}
				bordereau={bordereau}
				isSuperUser={isSuperUser}
			/>
		);
	}

	if (data?.mettreEnEchecModal) {
		return (
			<BordereauMettreEnEchecModal
				bordereau={bordereau}
				isSuperUser={isSuperUser}
				onChange={(updatedBordereau: Bordereau) =>
					onChange(BordereauActionType.METTRE_EN_ECHEC, updatedBordereau)
				}
				onClose={() => handleCloseModal('mettreEnEchecModal')}
				visible={data.mettreEnEchecModal.visible}
			/>
		);
	}

	if (data?.priseEnChargeModal?.visible) {
		return (
			<BordereauForcePriseEnChargeModal
				visible={data?.priseEnChargeModal?.visible}
				bordereau={bordereau}
				onChange={(bordereauModifie) =>
					onChange(BordereauActionType.FORCE_TRAITE_DGFIP, bordereauModifie)
				}
				onClose={() => handleCloseModal('priseEnChargeModal')}
				isSuperUser={isSuperUser}
			/>
		);
	}

	return null;
};

export default BordereauModalProvider;
